import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Flex,
  HStack,
  VStack,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatGroup,
  Button,
  Input,
  useColorModeValue,
  Select,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Checkbox,
  useDisclosure,
  useToast,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import Chart from "react-apexcharts";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabaseClient";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  QuestionIcon,
} from "@chakra-ui/icons";
import SendEmail from "../../components/SendEmail";
import ChannelDetailModal from "./ChannelDetailModal";
import { fetchWithToken } from "../../lib/api";
// 기준일(오늘-3일) 계산 함수
function getBaseDate() {
  const today = new Date();
  today.setDate(today.getDate() - 3);
  return today;
}

// 기본 기간 반환 함수
function getDefaultDates(period = 7) {
  const base = getBaseDate();
  const end = base.toISOString().slice(0, 10);
  const startDateObj = new Date(base);
  startDateObj.setDate(base.getDate() - period);
  const start = startDateObj.toISOString().slice(0, 10);
  return { start, end };
}

const RevenueByChannel = () => {
  const { user } = useAuth();
  const [inputDateRange, setInputDateRange] = useState(getDefaultDates(7));
  const [dateRange, setDateRange] = useState(getDefaultDates(7));
  const [period, setPeriod] = useState(7); // 7, 14, 21, 30
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [chart1Data, setChart1Data] = useState({
    categories: [],
    revenue: [],
    views: [],
    premiumViews: [],
  });

  // 채널별 표 관련 상태
  const [channelList, setChannelList] = useState([]);
  const [channelLoading, setChannelLoading] = useState(false);
  const [channelError, setChannelError] = useState("");
  const [inputSorting, setInputSorting] = useState("TOTAL_VIEWS");
  const [pageNumber, setPageNumber] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [inputSearchKeywords, setInputSearchKeywords] = useState("");
  const [searchParams, setSearchParams] = useState({
    dateRange: getDefaultDates(7),
    searchKeywords: "",
    sorting: "TOTAL_VIEWS",
    pageNumber: 1,
  });

  const [hasContract, setHasContract] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    channel: "",
    agree: false,
  });
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const isFormValid =
    form.name && form.email && form.phone && form.channel && form.agree;

  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channelModalOpen, setChannelModalOpen] = useState(false);
  const [channelInfo, setChannelInfo] = useState(null);
  const [channelInfoLoading, setChannelInfoLoading] = useState(false);
  const [channelVideos, setChannelVideos] = useState([]);
  const [channelVideosPagination, setChannelVideosPagination] = useState(null);
  const [channelVideosLoading, setChannelVideosLoading] = useState(false);
  const [channelVideosSearch, setChannelVideosSearch] = useState("");
  const [channelVideosPage, setChannelVideosPage] = useState(1);

  const [tempPage, setTempPage] = useState(pageNumber.toString());
  useEffect(() => {
    setTempPage(pageNumber.toString());
  }, [pageNumber]);
  const handlePageInput = (e) => setTempPage(e.target.value);
  const handlePageMove = () => {
    if (/^\d+$/.test(tempPage)) {
      const page = parseInt(tempPage);
      if (page >= 1 && page <= (pagination?.totalPages || 1))
        handlePageChange(page);
      else setTempPage(pageNumber.toString());
    } else setTempPage(pageNumber.toString());
  };

  // 실제 데이터 가져오기
  useEffect(() => {
    if (!searchParams.dateRange) return;
    const fetchSummary = async () => {
      if (!user?.email) return;
      setLoading(true);
      setError("");
      try {
        // 1. user 테이블에서 id 조회
        const { data, error } = await supabase
          .from("user")
          .select("id")
          .eq("email", user.email)
          .single();
        if (error || !data?.id) throw new Error("user_id 조회 실패");
        const userId = data.id;
        // 2. access_token 가져오기
        const accessToken = localStorage.getItem("access_token");
        // 3. API 요청
        const params = new URLSearchParams({
          user_id: userId,
          start_date: searchParams.dateRange.start,
          end_date: searchParams.dateRange.end,
        });
        const response = await fetchWithToken(
          `https://plpl.rhoonart.co.kr/profits/total?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result = await response.json();
        if (!result?.data?.summary) throw new Error("데이터 없음");
        setSummary(result.data.summary);
        // dailyStats 차트 데이터 가공
        const dailyStats = result?.data?.dailyStats || [];
        setChart1Data({
          categories: dailyStats.map((d) => d.date),
          revenue: dailyStats.map((d) => d.revenueKrw),
          views: dailyStats.map((d) => d.views),
          premiumViews: dailyStats.map((d) => d.premiumViews),
        });
      } catch (e) {
        setError("데이터");
        setSummary(null);
        setChart1Data({
          categories: [],
          revenue: [],
          views: [],
          premiumViews: [],
        });
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
    // eslint-disable-next-line
  }, [user, searchParams.dateRange]);

  // 채널별 표 fetch
  useEffect(() => {
    const fetchChannelList = async () => {
      if (!user?.email) return;
      setChannelLoading(true);
      setChannelError("");
      try {
        const { data, error } = await supabase
          .from("user")
          .select("id")
          .eq("email", user.email)
          .single();
        if (error || !data?.id) throw new Error("user_id 조회 실패");
        const accessToken = localStorage.getItem("access_token");
        if (!accessToken) throw new Error("access_token 없음");
        const userId = data.id;
        const params = new URLSearchParams({
          start_date: searchParams.dateRange.start,
          end_date: searchParams.dateRange.end,
          user_id: userId,
          sorting: searchParams.sorting,
          order: "DESC",
          pageNumber: searchParams.pageNumber,
          searchKeywords: searchParams.searchKeywords,
        });
        const response = await fetchWithToken(
          `https://plpl.rhoonart.co.kr/profits/channel?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result = await response.json();
        if (!result?.data?.channelList) throw new Error("데이터 없음");
        setChannelList(result.data.channelList);
        setPagination(result.data.pagination);
      } catch (e) {
        setChannelError(e.message || "데이터 로드 실패");
        setChannelList([]);
        setPagination(null);
      } finally {
        setChannelLoading(false);
      }
    };
    fetchChannelList();
    // eslint-disable-next-line
  }, [user, searchParams]);

  useEffect(() => {
    const checkContract = async () => {
      if (!user?.email) return setHasContract(false);
      const { data, error } = await supabase
        .from("shorts_contract")
        .select("id")
        .eq("contractor", user.email)
        .maybeSingle();
      if (error) {
        setHasContract(false);
        return;
      }
      setHasContract(!!data);
    };
    checkContract();
  }, [user]);

  // 카드 데이터
  const stats = [
    {
      label: "총 수익 (KRW)",
      value: summary?.totalRevenueKrw
        ? `₩${summary.totalRevenueKrw.toLocaleString()}`
        : "-",
    },
    {
      label: "총 수익 (USD)",
      value: summary?.totalRevenueUsd
        ? `$ ${summary.totalRevenueUsd.toLocaleString()}`
        : "-",
    },
    {
      label: "총 영상 수",
      value: summary?.totalVideos
        ? `${summary.totalVideos.toLocaleString()}개`
        : "-",
    },
    {
      label: "총 조회수",
      value: summary?.totalViews
        ? `${summary.totalViews.toLocaleString()}회`
        : "-",
      hasTooltip: true,
    },
  ];

  // 첫 번째 차트 데이터 (실제 데이터)
  const chart1 = {
    options: {
      chart: { id: "revenue-views", toolbar: { show: true } },
      xaxis: { categories: chart1Data.categories },
      colors: ["#1e90ff", "#00e676", "#ffc107"],
      stroke: { width: 4 },
      legend: { show: true, position: "bottom" },
      yaxis: {
        labels: {
          formatter: function (value) {
            return value.toLocaleString();
          },
        },
      },
    },
    series: [
      { name: "수익(KRW)", data: chart1Data.revenue },
      { name: "조회수", data: chart1Data.views },
      { name: "[프리미엄] 조회수", data: chart1Data.premiumViews },
    ],
  };

  // 기간 변경 핸들러
  const handlePeriodChange = (days) => {
    setPeriod(days);
    const newRange = getDefaultDates(days);
    setInputDateRange(newRange);
    setSearchParams((prev) => ({
      ...prev,
      dateRange: newRange,
      pageNumber: 1,
    }));
    setPageNumber(1);
  };

  // 검색 버튼 클릭 시
  const handleSearch = () => {
    setSearchParams({
      dateRange: inputDateRange,
      searchKeywords: inputSearchKeywords,
      sorting: inputSorting,
      pageNumber: 1,
    });
    setPageNumber(1);
  };

  // 정렬 변경 시 바로 데이터 조회
  const handleSortingChange = (value) => {
    setInputSorting(value);
    setSearchParams((prev) => ({
      ...prev,
      sorting: value,
      pageNumber: 1,
    }));
    setPageNumber(1);
  };

  // 페이지네이션 버튼 클릭 시만 searchParams.pageNumber 변경
  const handlePageChange = (newPage) => {
    setSearchParams((prev) => ({ ...prev, pageNumber: newPage }));
    setPageNumber(newPage);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    setIsLoadingModal(true);
    try {
      await SendEmail({
        email: "contact@plpl.kr",
        ccEmails: user?.email ? [user.email] : [],
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      await SendEmail({
        email: "jaeeui.kwon@rhoonart.com",
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      await SendEmail({
        email: "blair.park@laeebly.com",
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      await SendEmail({
        email: "kirby.lee@laeebly.com",
        subject: `계약 상담 신청 / ${user?.email}`,
        message: `<p>성함: ${form.name}</p>
        <p>이메일: ${form.email}</p>
        <p>연락처: ${form.phone}</p>
        <p>유튜브 채널: ${form.channel}</p>`,
      });
      toast({
        title: "상담 요청이 접수되었습니다.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setForm({ name: "", email: "", phone: "", channel: "", agree: false });
      onClose();
    } catch (e) {
      toast({
        title: "상담 요청에 실패했습니다.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingModal(false);
    }
  };

  // 채널 row 클릭 핸들러
  const handleChannelRowClick = (channel) => {
    setSelectedChannel(channel);
    setChannelModalOpen(true);
  };

  // 채널 상세 정보 fetch
  useEffect(() => {
    if (!selectedChannel || !channelModalOpen) return;
    const fetchChannelInfo = async () => {
      setChannelInfoLoading(true);
      try {
        const accessToken = localStorage.getItem("access_token");
        const params = new URLSearchParams({
          channel_id: selectedChannel.channelId,
          start_date: searchParams.dateRange.start,
          end_date: searchParams.dateRange.end,
        });
        const res = await fetchWithToken(
          `https://plpl.rhoonart.co.kr/profits/channel/info?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result = await res.json();
        setChannelInfo(result.data);
      } catch (e) {
        setChannelInfo(null);
      } finally {
        setChannelInfoLoading(false);
      }
    };
    fetchChannelInfo();
  }, [selectedChannel, channelModalOpen, searchParams.dateRange]);

  // 채널 영상 리스트 fetch
  useEffect(() => {
    if (!selectedChannel || !channelModalOpen) return;
    const fetchChannelVideos = async () => {
      setChannelVideosLoading(true);
      try {
        const accessToken = localStorage.getItem("access_token");
        const params = new URLSearchParams({
          channel_id: selectedChannel.channelId,
          start_date: searchParams.dateRange.start,
          end_date: searchParams.dateRange.end,
          searchKeywords: channelVideosSearch,
          pageNumber: channelVideosPage,
        });
        const res = await fetchWithToken(
          `https://plpl.rhoonart.co.kr/profits/channel/videos?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result = await res.json();
        setChannelVideos(result.data.shortsList);
        setChannelVideosPagination(result.data.pagination);
      } catch (e) {
        setChannelVideos([]);
        setChannelVideosPagination(null);
      } finally {
        setChannelVideosLoading(false);
      }
    };
    fetchChannelVideos();
  }, [
    selectedChannel,
    channelModalOpen,
    searchParams.dateRange,
    channelVideosSearch,
    channelVideosPage,
  ]);

  const accessToken =
    typeof window !== "undefined" ? localStorage.getItem("access_token") : "";

  if (hasContract === null || loading === true) {
    return (
      <Flex minH="60vh" align="center" justify="center" direction="column">
        <Spinner size="xl" color="teal.500" />
      </Flex>
    );
  }
  if (hasContract === false) {
    return (
      <Flex minH="60vh" direction="column" align="center" justify="center">
        <Text fontSize="md" color="gray.700" mb={2}>
          신규 회원은 레이블리와 계약 체결 후 이용이 가능합니다.
        </Text>
        <Button colorScheme="yellow" size="lg" mt={2} onClick={onOpen}>
          계약 상담 신청하기
        </Button>
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay />
          <ModalContent maxW="lg">
            <ModalHeader>레이블리 이용 계약 문의하기</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={4} fontSize="md">
                신규 회원은 개별 계약 후 이용이 가능합니다. 이용 계약 상담을
                신청해주시면, 기재해주신 연락처로 연락드리겠습니다.
                <br />
                (이메일만 기재할 경우, 스팸으로 분류되는 경우가 많습니다. 이용
                계약 문의주시면, 연락처로 연락드립니다.)
              </Text>
              <FormControl mb={3} isRequired>
                <FormLabel>성함 (필수)</FormLabel>
                <Input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="성함"
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>이메일 (필수)</FormLabel>
                <Input
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="이메일"
                  type="email"
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>연락처 (필수)</FormLabel>
                <Input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="연락처"
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>운영중인 유튜브 채널 (필수)</FormLabel>
                <Input
                  name="channel"
                  value={form.channel}
                  onChange={handleChange}
                  placeholder="유튜브 채널"
                />
              </FormControl>
              <Checkbox
                name="agree"
                isChecked={form.agree}
                onChange={handleChange}
                mb={2}
              >
                (필수) 레이블리 이용 상담을 위한 개인정보 제공에 동의합니다.
              </Checkbox>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="blue"
                onClick={handleSubmit}
                isLoading={isLoadingModal}
                isDisabled={!isFormValid}
                w="100%"
                size="lg"
              >
                상담 요청하기
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Flex>
    );
  }

  return (
    <Box bg="gray.50" minH="100vh" py={8}>
      <Container maxW="container.xl">
        {/* 상단 요약 카드 */}
        <StatGroup mb={6} gap={4}>
          {stats.map((stat, idx) => (
            <Stat
              key={stat.label}
              px={6}
              py={4}
              bg="white"
              borderRadius="xl"
              boxShadow="md"
              minW="220px"
            >
              <Flex align="center" gap={1}>
                <StatLabel fontSize="md" color="gray.600">
                  {stat.label}
                </StatLabel>
                {stat.hasTooltip && (
                  <Tooltip
                    label="유효 조회수 기준으로 데이터 제공"
                    placement="top"
                    hasArrow
                    bg="gray.800"
                    color="white"
                    borderRadius="md"
                    fontSize="sm"
                    px={3}
                    py={2}
                  >
                    <IconButton
                      icon={<QuestionIcon />}
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      aria-label="조회수 정보"
                      minW="auto"
                      h="auto"
                      p={1}
                      _hover={{
                        bg: "blue.50",
                        color: "blue.500",
                        transform: "scale(1.1)",
                      }}
                      transition="all 0.2s"
                    />
                  </Tooltip>
                )}
              </Flex>
              <StatNumber fontSize="2xl" color="black">
                {stat.value}
              </StatNumber>
            </Stat>
          ))}
        </StatGroup>

        {/* 첫 번째 차트 */}
        <Box bg="white" borderRadius="xl" boxShadow="md" p={4} mb={8}>
          <Chart
            options={chart1.options}
            series={chart1.series}
            type="line"
            height={320}
          />
        </Box>

        {/* 기간 필터 */}
        <Flex align="center" gap={4} mb={4}>
          <Select
            value={inputSorting}
            onChange={(e) => handleSortingChange(e.target.value)}
            maxW="180px"
            bg="gray.50"
          >
            <option value="TOTAL_VIEWS">조회수순</option>
            <option value="REVENUE_KRW">KRW 수익순</option>
          </Select>
          <Select
            value={period}
            onChange={(e) => handlePeriodChange(Number(e.target.value))}
            maxW="120px"
            bg="white"
          >
            <option value={7}>최근 7일</option>
            <option value={14}>최근 14일</option>
            <option value={21}>최근 21일</option>
            <option value={30}>최근 30일</option>
          </Select>
          <Input
            type="date"
            value={inputDateRange.start}
            onChange={(e) =>
              setInputDateRange((r) => ({ ...r, start: e.target.value }))
            }
            maxW="160px"
            bg="white"
          />
          <Text>~</Text>
          <Input
            type="date"
            value={inputDateRange.end}
            onChange={(e) =>
              setInputDateRange((r) => ({ ...r, end: e.target.value }))
            }
            maxW="160px"
            bg="white"
          />
          <Input
            placeholder="채널명 검색"
            value={inputSearchKeywords}
            onChange={(e) => setInputSearchKeywords(e.target.value)}
            maxW="220px"
            bg="white"
          />
          <Button colorScheme="teal" onClick={handleSearch}>
            검색
          </Button>
        </Flex>

        {/* 채널별 정보 표 */}
        <Box bg="white" borderRadius="xl" boxShadow="md" p={4} mb={8}>
          <Flex mb={4} align="center" gap={4} justify="space-between">
            <Text color="gray.500" fontSize="sm">
              채널별 상세 수익 및 통계
            </Text>
          </Flex>
          <Box overflowX="auto">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <colgroup>
                <col style={{ width: "4%" }} />
                <col style={{ width: "27%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "15%" }} />
              </colgroup>
              <thead style={{ background: "#f7fafc" }}>
                <tr>
                  <th
                    style={{
                      padding: "12px",
                      fontSize: 13,
                      wordBreak: "keep-all",
                      whiteSpace: "normal",
                    }}
                  >
                    #
                  </th>
                  <th
                    style={{
                      fontSize: 13,
                      wordBreak: "keep-all",
                      whiteSpace: "normal",
                    }}
                  >
                    채널 정보
                  </th>
                  <th
                    style={{
                      fontSize: 13,
                      wordBreak: "keep-all",
                      whiteSpace: "normal",
                    }}
                  >
                    영상 수
                  </th>
                  <th
                    style={{
                      fontSize: 13,
                      wordBreak: "keep-all",
                      whiteSpace: "normal",
                    }}
                  >
                    <Flex align="center" gap={1} justify="center">
                      <span>
                        조회수
                        <br />
                        (프리미엄 조회수)
                      </span>
                      <Tooltip
                        label={
                          <Box>
                            <Text>
                              플플 대시보드의 '조회수'는 유튜브 스튜디오의 '전체
                              조회수'와 달리{" "}
                              <Text
                                fontWeight="bold"
                                color="blue.600"
                                as="span"
                              >
                                유효 조회수 기준으로 제공
                              </Text>
                              <Text as="span"> 되며, </Text>
                            </Text>

                            <Text>
                              <Text
                                as="span"
                                fontWeight="bold"
                                color="blue.600"
                              >
                                프리미엄 조회수도 유효 조회수 기준으로만 집계
                              </Text>
                              <Text as="span"> 됩니다.</Text>
                            </Text>
                            <Text mt={2}>
                              이로 인해 스튜디오에서 확인하시는 조회수와 다를 수
                              있으며,
                            </Text>
                            <Text>
                              이는 집계 기준의 차이로 발생하는 현상이니 대시보드
                              확인 시 참고해주시기 바랍니다.
                            </Text>
                          </Box>
                        }
                        placement="top"
                        hasArrow
                        bg="white"
                        color="black"
                        borderRadius="lg"
                        fontSize="sm"
                        px={3}
                        py={2}
                        maxW="650px"
                        border="1px solid"
                        textAlign="center"
                        borderColor="gray.200"
                      >
                        <IconButton
                          icon={<QuestionIcon />}
                          size="xs"
                          variant="ghost"
                          colorScheme="blue"
                          aria-label="조회수 정보"
                          minW="auto"
                          h="auto"
                          p={1}
                          _hover={{
                            bg: "blue.50",
                            color: "blue.500",
                            transform: "scale(1.1)",
                          }}
                          transition="all 0.2s"
                        />
                      </Tooltip>
                    </Flex>
                  </th>
                  <th
                    style={{
                      fontSize: 13,
                      wordBreak: "keep-all",
                      whiteSpace: "normal",
                    }}
                  >
                    시청 시간
                    <br />
                    (프리미엄 시청 시간)
                  </th>
                  <th
                    style={{
                      fontSize: 13,
                      wordBreak: "keep-all",
                      whiteSpace: "normal",
                    }}
                  >
                    US 예상 수익
                  </th>
                  <th
                    style={{
                      fontSize: 13,
                      wordBreak: "keep-all",
                      whiteSpace: "normal",
                    }}
                  >
                    KRW 예상 수익 (단가)
                  </th>
                </tr>
              </thead>
              <tbody>
                {channelLoading ? (
                  <tr>
                    <td
                      colSpan={7}
                      style={{ textAlign: "center", padding: 32 }}
                    >
                      <Spinner size="lg" color="teal.400" />
                    </td>
                  </tr>
                ) : channelError ? (
                  <tr>
                    <td
                      colSpan={7}
                      style={{ textAlign: "center", color: "red", padding: 32 }}
                    >
                      {channelError}
                    </td>
                  </tr>
                ) : channelList.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      style={{
                        textAlign: "center",
                        color: "#888",
                        padding: 32,
                      }}
                    >
                      데이터가 없습니다.
                    </td>
                  </tr>
                ) : (
                  channelList.map((channel, idx) => (
                    <tr
                      key={idx}
                      style={{
                        borderBottom: "1px solid #eee",
                        cursor: "pointer",
                      }}
                      onClick={() => handleChannelRowClick(channel)}
                    >
                      <td style={{ textAlign: "center", fontWeight: 600 }}>
                        {idx + 1}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          wordBreak: "break-all",
                          whiteSpace: "normal",
                        }}
                      >
                        <img
                          src={channel.channelThumbnailUrl}
                          alt={channel.channelTitle}
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {channel.channelTitle || (
                              <>
                                채널정보확인불가
                                <span
                                  style={{
                                    background: "#e53e3e",
                                    color: "white",
                                    borderRadius: "8px",
                                    fontSize: "11px",
                                    padding: "2px 8px",
                                    marginLeft: "8px",
                                    verticalAlign: "middle",
                                    fontWeight: 700,
                                  }}
                                >
                                  미등록 채널
                                </span>
                              </>
                            )}
                          </div>
                          <div style={{ color: "#888", fontSize: 13 }}>
                            {channel.channelId}
                          </div>
                        </div>
                      </td>
                      <td
                        style={{
                          textAlign: "center",
                          wordBreak: "break-all",
                          whiteSpace: "normal",
                        }}
                      >
                        {channel.totalVideos?.toLocaleString()}
                      </td>
                      <td
                        style={{
                          textAlign: "center",
                          wordBreak: "break-all",
                          whiteSpace: "normal",
                        }}
                      >
                        {channel.totalViews?.toLocaleString()}
                        <br />
                        <span style={{ color: "#4fd1c5", fontSize: 13 }}>
                          ({channel.totalPremiumViews?.toLocaleString()}
                          {/*{(channel.premiumViewRatio * 100).toFixed(2)}%*/})
                        </span>
                      </td>
                      <td
                        style={{
                          textAlign: "center",
                          wordBreak: "break-all",
                          whiteSpace: "normal",
                        }}
                      >
                        {channel.totalWatchTime?.toLocaleString()}
                        <br />
                        <span style={{ color: "#4fd1c5", fontSize: 13 }}>
                          ({channel.totalPremiumWatchTime?.toLocaleString()}
                          {/*{(channel.premiumWatchTimeRatio * 100).toFixed(2)}%*/}
                          )
                        </span>
                      </td>
                      <td
                        style={{
                          textAlign: "center",
                          whiteSpace: "pre-line",
                          wordBreak: "break-all",
                        }}
                      >
                        $ {channel.estimatedRevenueUsd?.toLocaleString()}
                        <br />
                        <span style={{ color: "#4fd1c5", fontSize: 13 }}>
                          {/*$ {channel.usdRevenuePerView}*/}
                        </span>
                      </td>
                      <td
                        style={{
                          textAlign: "center",
                          whiteSpace: "pre-line",
                          wordBreak: "break-all",
                        }}
                      >
                        ₩ {channel.estimatedRevenueKrw?.toLocaleString()}
                        <br />
                        <span style={{ color: "#4fd1c5", fontSize: 13 }}>
                          ₩ {channel.krwRevenuePerView}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>
          {/* 페이지네이션 */}
          {pagination && (
            <Flex justify="center" align="center" mt={6} gap={2}>
              <Button
                onClick={() => handlePageChange(1)}
                isDisabled={pageNumber === 1}
                size="sm"
                variant="outline"
              >
                처음
              </Button>
              <Button
                onClick={() => handlePageChange(Math.max(1, pageNumber - 1))}
                isDisabled={pageNumber === 1}
                size="sm"
                variant="outline"
              >
                이전
              </Button>
              <HStack spacing={1} mx={2}>
                <Input
                  value={tempPage}
                  onChange={handlePageInput}
                  size="sm"
                  width="50px"
                  textAlign="center"
                  fontSize="sm"
                />
                <Button
                  size="sm"
                  onClick={handlePageMove}
                  colorScheme="gray"
                  variant="outline"
                  minW="40px"
                  px={2}
                  color="gray.600"
                  borderColor="gray.300"
                  _hover={{ bg: "gray.100", borderColor: "gray.400" }}
                >
                  이동
                </Button>
                <Text fontWeight="bold">/ {pagination.totalPages}</Text>
              </HStack>
              <Button
                onClick={() =>
                  handlePageChange(
                    Math.min(pagination.totalPages, pageNumber + 1)
                  )
                }
                isDisabled={pageNumber === pagination.totalPages}
                size="sm"
                variant="outline"
              >
                다음
              </Button>
              <Button
                onClick={() => handlePageChange(pagination.totalPages)}
                isDisabled={pageNumber === pagination.totalPages}
                size="sm"
                variant="outline"
              >
                마지막
              </Button>
            </Flex>
          )}
        </Box>

        {/* 채널 상세 모달 분리 */}
        <ChannelDetailModal
          isOpen={channelModalOpen}
          onClose={() => setChannelModalOpen(false)}
          channel={selectedChannel}
          dateRange={searchParams.dateRange}
          accessToken={accessToken}
          userId={user?.id}
        />
      </Container>
    </Box>
  );
};

export default RevenueByChannel;
